import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRecentWantedAds } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardList, MapPin, Calendar, Package, ArrowRight, PlusCircle } from 'lucide-react';
import { WantedAdDetailDialog } from '@/components/wanted/WantedAdDetailDialog';

const formatPrice = (min, max) => {
  if (min == null && max == null) return null;
  const fmt = (v) => `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `ab ${fmt(min)}`;
  return `bis ${fmt(max)}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export function RecentWantedAdsStrip({ onPostClick }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAd, setSelectedAd] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getRecentWantedAds(5);
        setAds(res.data?.ads || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <section data-testid="recent-wanted-ads-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-manrope text-2xl font-bold flex items-center gap-2">
          <span className="w-1 h-6 bg-amber-500 rounded-full" />
          <ClipboardList className="h-6 w-6 text-amber-500" />
          Aktuelle Gesuche
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50"
            onClick={onPostClick}
            data-testid="home-post-wanted-ad-btn"
          >
            <PlusCircle className="h-4 w-4" />
            Gesuch einstellen
          </Button>
          <Button asChild variant="ghost" size="sm" className="gap-1">
            <Link to="/wanted" data-testid="home-view-all-wanted-btn">
              Alle ansehen <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
        </div>
      ) : ads.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            Aktuell keine offenen Gesuche — <button onClick={onPostClick} className="text-orange-600 font-medium hover:underline" data-testid="home-post-wanted-ad-empty-btn">stellen Sie das erste ein</button>.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {ads.map((ad) => {
            const priceStr = formatPrice(ad.price_min_eur, ad.price_max_eur);
            return (
              <Card
                key={ad.wanted_id}
                className="hover:shadow-md hover:border-amber-300 transition-all cursor-pointer"
                data-testid={`home-wanted-ad-${ad.wanted_id}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedAd(ad)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedAd(ad); } }}
              >
                <CardContent className="p-4">
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] mb-2">
                    {ad.category_name}
                  </Badge>
                  <h3 className="font-semibold text-sm leading-tight line-clamp-2 mb-2">
                    {ad.material_type_description}
                  </h3>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Package className="h-3 w-3 text-blue-500 shrink-0" />
                      <span>{ad.min_quantity} {ad.quantity_unit}</span>
                    </div>
                    {priceStr && (
                      <div className="flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-400">
                        <span className="inline-block w-3 text-center">€</span>
                        <span>{priceStr}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-green-500 shrink-0" />
                      <span>{[ad.poster_postal_code, ad.poster_country].filter(Boolean).join(', ') || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-orange-500 shrink-0" />
                      <span>bis {formatDate(ad.valid_until)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <WantedAdDetailDialog
        ad={selectedAd}
        open={!!selectedAd}
        onClose={() => setSelectedAd(null)}
      />
    </section>
  );
}
